import React, { useEffect } from "react";
import PageShell from "../components/PageShell";
import { setMeta } from "../lib/seo";

export default function Privacy() {
  useEffect(() => {
    setMeta({
      title: "Privacy Policy — Culinera",
      description:
        "How Culinera collects, uses, and protects your information. We don't require an email to browse, we don't sell personal information, and you can request deletion anytime.",
      canonical: "https://culinera.ca/privacy",
    });
  }, []);

  return (
    <PageShell testid="privacy-page">
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-6">Last updated: November 3, 2026</p>
        <p className="text-gray-700 mb-6">
          Culinera ("we", "us") operates culinera.ca. This policy explains what information we collect, how we use it,
          and your choices. Questions about privacy go to{" "}
          <a className="text-teal-600 hover:underline" href="mailto:hello@culinera.ca">hello@culinera.ca</a>.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">Information we collect</h2>
        <ul className="list-disc pl-6 space-y-2 text-gray-700">
          <li><strong>Trip-planning details you enter in the wizard:</strong> travel dates, party size, food preferences, and the places you save. We use these only to build your itinerary.</li>
          <li><strong>Your email address</strong>, only when you choose to provide it (currently at the wizard's contact step) so we can send your personalized itinerary to you. We do not require an email to browse the site, read guides, or view sample itineraries.</li>
          <li><strong>Messages you send us</strong> at hello@culinera.ca, including listing corrections and support questions.</li>
          <li><strong>Cookies and usage data:</strong> the site uses cookies for basic functionality. We do not currently use third-party analytics. If advertising is shown, Google and its partners may use cookies to serve ads based on your visits to this and other sites. You can control ad cookies through Google's Ads Settings and your browser's cookie settings.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">How we use information</h2>
        <p className="text-gray-700 mb-4">
          We use the information above to operate the site, build and deliver itineraries you request, respond to your
          messages, and improve the guides. We do not sell your personal information to anyone.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">Who processes it</h2>
        <p className="text-gray-700 mb-4">
          Your information is processed only by the services needed to run the site: Gmail (provided by Google LLC) to
          send the itineraries you request, and our managed cloud hosting provider to run the site. We do not currently
          use a third-party analytics service. These services process data only on our instructions.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">How long we keep it</h2>
        <p className="text-gray-700 mb-4">
          Itineraries are generated on demand and emailed to you, so we do not store the trip details or email address
          you enter in the wizard in our database after your itinerary has been sent. Trips you explicitly choose to save
          or share are kept until you ask us to delete them. You can request deletion of your information at any time by
          writing to <a className="text-teal-600 hover:underline" href="mailto:hello@culinera.ca">hello@culinera.ca</a>.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">Your choices</h2>
        <p className="text-gray-700 mb-4">
          You can browse without providing an email, block or delete cookies in your browser (some features, like saving
          a trip, may not work without them), and request a copy or deletion of your information at hello@culinera.ca.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">Children</h2>
        <p className="text-gray-700 mb-4">
          Culinera is a general-audience travel site. We do not knowingly collect personal information from children
          under 13. If you believe a child has provided information, contact us and we will delete it.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">Changes</h2>
        <p className="text-gray-700 mb-4">
          If this policy changes materially, we will update the date above and post the new version here.
        </p>
      </article>
    </PageShell>
  );
}
